import { useState, useEffect } from "react";
import { toast } from "sonner";
import { FileText, Download, Search, Cloud, Database } from "lucide-react";
import { useAuth, API } from "../../context/AuthContext";

/**
 * Standalone browser for every SEPA Direct Debit mandate stored on file.
 * Distinct from the approval workflow (PendingApprovals) — this stays available
 * long after a mandate has been approved, so FA can go back and download the
 * PDF at any time from a single place.
 *
 * The PDF itself is served by /api/wallet/sepa-mandate/{member_id}/pdf which prefers
 * object storage and falls back to inline base64 for legacy records.
 */
export default function SepaMandatesBrowser() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);
  const { authAxios } = useAuth();

  useEffect(() => {
    authAxios.get("/finance-admin/sepa-mandates")
      .then(r => setItems(r.data || []))
      .catch(e => toast.error(e.response?.data?.detail || "Failed to load mandates"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = items.filter(m => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (m.name || "").toLowerCase().includes(q)
      || (m.email || "").toLowerCase().includes(q)
      || (m.member_id || "").toLowerCase().includes(q);
  });

  const download = async (mid) => {
    setDownloadingId(mid);
    try {
      const r = await fetch(`${API}/wallet/sepa-mandate/${mid}/pdf`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SEPA-Mandat-${mid}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "\u2014";
    try { return new Date(iso).toLocaleDateString(); }
    catch { return iso; }
  };

  return (
    <div data-testid="sepa-mandates-browser">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h2 className="text-xl font-bold text-[#1e3a5f] flex items-center gap-2">
          <FileText className="w-5 h-5" /> SEPA Mandates
        </h2>
        <span className="text-xs text-slate-400">Every SEPA Direct Debit PDF on file — browsable and downloadable.</span>
      </div>

      <div className="mb-4 flex items-center gap-2 bg-white rounded-xl border border-slate-200 px-3 py-2 max-w-sm">
        <Search className="w-4 h-4 text-slate-400" />
        <input
          type="search"
          placeholder="Search by name, email or member ID"
          value={query}
          onChange={e => setQuery(e.target.value)}
          className="flex-1 text-sm outline-none bg-transparent"
          data-testid="sepa-mandates-search"
        />
      </div>

      {loading ? (
        <p className="text-slate-400 text-center py-8">Loading mandates...</p>
      ) : filtered.length === 0 ? (
        <p className="text-slate-400 text-center py-8">
          {items.length === 0 ? "No SEPA mandates on file yet." : "No mandates match your search."}
        </p>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 border-b">
                <th className="px-4 py-3">Member</th>
                <th className="px-4 py-3">Member ID</th>
                <th className="px-4 py-3">Mandate date</th>
                <th className="px-4 py-3">Storage</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(m => (
                <tr key={m.member_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`sepa-mandate-row-${m.member_id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-[#1e3a5f]">{m.name || "\u2014"}</p>
                    <p className="text-xs text-slate-500">{m.email || ""}</p>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{m.member_id}</td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(m.mandate_date)}</td>
                  <td className="px-4 py-3">
                    {m.in_storage ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 text-[10px] font-medium">
                        <Cloud className="w-3 h-3" /> Object storage
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-medium" title="Legacy inline PDF (base64)">
                        <Database className="w-3 h-3" /> Inline
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => download(m.member_id)}
                      disabled={downloadingId === m.member_id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#1e3a5f] text-white text-xs font-medium disabled:opacity-50"
                      data-testid={`sepa-download-btn-${m.member_id}`}
                    >
                      <Download className="w-3.5 h-3.5" />
                      {downloadingId === m.member_id ? "..." : "Download"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
